/**
 * Pages feature flags — no screen api_name / page-id allowlists in JS.
 *
 * Source of truth (in order):
 * 1. Markup button/input attributes (buildShipmentArray, tailgatePreset, …)
 * 2. URL query (?clearRuntime=1)
 * 3. pageData / API description: SHIPCRM_PAGES_FLAGS:{...}
 * 4. sessionStorage registries filled when a flagged config loads
 *
 * Seed flags with scripts/pages_add_shipment_20261014/70_pages_feature_flags.sql
 */

const FLAGS_MARKER = "SHIPCRM_PAGES_FLAGS:";
const FRESH_ENTRY_REGISTRY_KEY = "shipcrm_pages_fresh_entry_keys";
const RATES_RESULTS_REGISTRY_KEY = "shipcrm_pages_rates_results_keys";
const RATES_PAGE_ID_REGISTRY_KEY = "shipcrm_pages_rates_page_ids";
const SHIPMENT_EDIT_SESSION_KEY = "shipcrm_pages_shipment_edit_active";

export const isShipmentEditSessionActive = () => {
  try {
    return sessionStorage.getItem(SHIPMENT_EDIT_SESSION_KEY) === "1";
  } catch (e) {
    return false;
  }
};

export const setShipmentEditSessionActive = (active) => {
  try {
    if (active) {
      sessionStorage.setItem(SHIPMENT_EDIT_SESSION_KEY, "1");
    } else {
      sessionStorage.removeItem(SHIPMENT_EDIT_SESSION_KEY);
    }
  } catch (e) {
    // quota / private mode
  }
};

export const isTruthyPagesFlag = (raw) => {
  if (raw === true || raw === 1) {
    return true;
  }
  if (raw == null || raw === false || raw === 0) {
    return false;
  }
  const norm = String(raw).trim().toLowerCase();
  return norm === "true" || norm === "1" || norm === "on" || norm === "yes";
};

const readJsonRegistry = (storageKey) => {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.map((v) => String(v)).filter(Boolean)
      : [];
  } catch (e) {
    return [];
  }
};

const writeJsonRegistry = (storageKey, values) => {
  try {
    const unique = [...new Set((values || []).map((v) => String(v)).filter(Boolean))];
    sessionStorage.setItem(storageKey, JSON.stringify(unique));
  } catch (e) {
    // quota / private mode
  }
};

const addToRegistry = (storageKey, values) => {
  const next = [...readJsonRegistry(storageKey), ...(values || [])];
  writeJsonRegistry(storageKey, next);
};

/**
 * Parse SHIPCRM_PAGES_FLAGS:{...} from pages_configuration.description (or any string).
 */
export const parsePagesFeatureFlagsFromDescription = (description) => {
  if (description == null || description === "") {
    return {};
  }
  const text = String(description);
  const idx = text.indexOf(FLAGS_MARKER);
  if (idx === -1) {
    return {};
  }
  const jsonPart = text.slice(idx + FLAGS_MARKER.length).trim();
  const start = jsonPart.indexOf("{");
  if (start === -1) {
    return {};
  }
  let depth = 0;
  let end = -1;
  for (let i = start; i < jsonPart.length; i += 1) {
    const ch = jsonPart[i];
    if (ch === "{") {
      depth += 1;
    } else if (ch === "}") {
      depth -= 1;
      if (depth === 0) {
        end = i;
        break;
      }
    }
  }
  if (end === -1) {
    return {};
  }
  try {
    const parsed = JSON.parse(jsonPart.slice(start, end + 1));
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch (e) {
    return {};
  }
};

/** Merge flags from description, pages shell, and top-level config fields. */
export const readPagesFeatureFlags = (configEntry) => {
  const entry =
    configEntry && typeof configEntry === "object" && !Array.isArray(configEntry)
      ? configEntry
      : {};
  const fromDescription = parsePagesFeatureFlagsFromDescription(
    entry.description
  );
  const pages =
    entry.pages && typeof entry.pages === "object" && !Array.isArray(entry.pages)
      ? entry.pages
      : {};
  return {
    ...fromDescription,
    ...(pages.feature_flags && typeof pages.feature_flags === "object"
      ? pages.feature_flags
      : {}),
    clear_runtime_on_entry:
      pages.clear_runtime_on_entry ??
      pages.clearRuntimeOnEntry ??
      entry.clear_runtime_on_entry ??
      fromDescription.clear_runtime_on_entry,
    rates_results_page:
      pages.rates_results_page ??
      pages.ratesResultsPage ??
      entry.rates_results_page ??
      fromDescription.rates_results_page,
  };
};

/**
 * When a pages config loads: stamp flags onto pages shell + fill session registries.
 * Call from loadPageConfigCollection* so route entry / redirects stay name-free.
 */
export const applyPagesConfigFeatureFlags = (collections, configKey) => {
  if (!collections || typeof collections !== "object") {
    return collections;
  }
  const key = String(
    configKey || collections.configuration_api_name || collections.id || ""
  );
  const flags = readPagesFeatureFlags(collections);
  const pages = {
    ...(collections.pages && typeof collections.pages === "object"
      ? collections.pages
      : {}),
    feature_flags: flags,
  };
  const params =
    pages.params && typeof pages.params === "object" && !Array.isArray(pages.params)
      ? pages.params
      : {};
  const response =
    pages.response && typeof pages.response === "object" && !Array.isArray(pages.response)
      ? pages.response
      : {};
  const readModeFlag = (key) =>
    params[key] !== undefined && params[key] !== null && params[key] !== ""
      ? params[key]
      : response[key];
  const modeIsClone =
    isTruthyPagesFlag(readModeFlag("is_clone")) ||
    isTruthyPagesFlag(readModeFlag("pages_clone_mode")) ||
    String(readModeFlag("pages_mode") || "").trim().toLowerCase() === "clone";
  const modeIsEdit =
    !modeIsClone &&
    (isTruthyPagesFlag(readModeFlag("is_edit")) ||
      isTruthyPagesFlag(readModeFlag("pages_edit_mode")) ||
      String(readModeFlag("pages_mode") || "").trim().toLowerCase() === "edit" ||
      isTruthyPagesFlag(flags.pages_edit_mode));

  if (isTruthyPagesFlag(flags.clear_runtime_on_entry)) {
    pages.clear_runtime_on_entry = true;
    if (key) {
      addToRegistry(FRESH_ENTRY_REGISTRY_KEY, [key]);
    }
  }
  // Explicit mode from action-button params; clone must not activate edit session.
  if (modeIsEdit) {
    setShipmentEditSessionActive(true);
  } else if (modeIsClone || isTruthyPagesFlag(flags.clear_runtime_on_entry)) {
    setShipmentEditSessionActive(false);
  }
  if (isTruthyPagesFlag(flags.rates_results_page)) {
    pages.rates_results_page = true;
    if (key) {
      addToRegistry(RATES_RESULTS_REGISTRY_KEY, [key]);
    }
    const pageId = pages.id || collections.pages_id || collections.page_id;
    if (pageId) {
      addToRegistry(RATES_PAGE_ID_REGISTRY_KEY, [pageId]);
    }
  }
  // Host entry configs may declare which rates targets to register (DB, not JS).
  if (Array.isArray(flags.register_rates_results)) {
    addToRegistry(RATES_RESULTS_REGISTRY_KEY, flags.register_rates_results);
  }
  if (Array.isArray(flags.register_rates_page_ids)) {
    addToRegistry(RATES_PAGE_ID_REGISTRY_KEY, flags.register_rates_page_ids);
  }
  if (Array.isArray(flags.register_fresh_entry_keys)) {
    addToRegistry(FRESH_ENTRY_REGISTRY_KEY, flags.register_fresh_entry_keys);
  }
  return {
    ...collections,
    pages,
  };
};

export const isFreshEntryConfigKey = (configKey, pageDataEntry, search = "") => {
  if (configKey == null || configKey === "") {
    return false;
  }
  try {
    const params = new URLSearchParams(
      typeof search === "string" ? search : ""
    );
    if (
      isTruthyPagesFlag(params.get("clearRuntime")) ||
      isTruthyPagesFlag(params.get("freshEntry"))
    ) {
      return true;
    }
  } catch (e) {
    // ignore
  }
  const flags = readPagesFeatureFlags(pageDataEntry || {});
  if (isTruthyPagesFlag(flags.clear_runtime_on_entry)) {
    return true;
  }
  return readJsonRegistry(FRESH_ENTRY_REGISTRY_KEY).includes(String(configKey));
};

export const isRatesResultsConfig = (configApiName, configEntry) => {
  if (configEntry && isTruthyPagesFlag(readPagesFeatureFlags(configEntry).rates_results_page)) {
    return true;
  }
  if (configApiName == null || configApiName === "") {
    return false;
  }
  return readJsonRegistry(RATES_RESULTS_REGISTRY_KEY).includes(
    String(configApiName)
  );
};

export const isRatesResultsPageId = (pageId) => {
  if (pageId == null || pageId === "") {
    return false;
  }
  return readJsonRegistry(RATES_PAGE_ID_REGISTRY_KEY).includes(String(pageId));
};

/**
 * Markup: buildShipmentArray / data-scm-build-shipment-array="true".
 * No action-name fallback — apply 68_ship_now_build_shipment_array_attr.sql.
 */
export const shouldBuildShipmentArrayFromLoop = (attributes = {}) => {
  if (!attributes || typeof attributes !== "object") {
    return false;
  }
  const flagKeys = [
    "buildShipmentArray",
    "buildshipmentarray",
    "data-scm-build-shipment-array",
  ];
  for (let i = 0; i < flagKeys.length; i += 1) {
    const key = flagKeys[i];
    if (Object.prototype.hasOwnProperty.call(attributes, key)) {
      return isTruthyPagesFlag(attributes[key]);
    }
  }
  return false;
};
